import { Experiment } from "@amplitude/experiment-js-client";
import type { ExperimentConfig } from "@amplitude/experiment-js-client/dist/types/src/config";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { hasConsent, onConsentChange } from "../utils/cookieYesConsent";
import { Provider, liveContextValue, offContextValue } from "./context";

export const AmplitudeExperimentProvider = ({
	apiKey,
	children,
	...experimentConfig
}: {
	apiKey: string;
} & ExperimentConfig & { children: ReactNode }) => {
	const [value, setValue] = useState(offContextValue);

	useEffect(() => {
		const { debug } = experimentConfig;
		let client: ReturnType<
			typeof Experiment.initializeWithAmplitudeAnalytics
		> | null = null;
		let started = false;

		const start = async () => {
			if (started) return;
			started = true;
			client = Experiment.initializeWithAmplitudeAnalytics(
				apiKey,
				experimentConfig,
			);
			// Note: Experiment SDK does not expose setOptOut() — opt-out state is not
			// persisted by this SDK, so no clearing is needed on re-consent.
			await client.start();
			setValue(liveContextValue(client));
			if (debug) console.debug("Amplitude Experiment started.");
		};

		const stop = () => {
			if (client) {
				client.stop();
			}
			setValue(offContextValue);
			if (debug) console.debug("Amplitude Experiment stopped.");
		};

		// Check consent and start if granted
		if (hasConsent("analytics")) {
			start().catch(console.error);
		}

		// Listen for consent changes (grants and withdrawals)
		// Rejection takes precedence if both arrays contain "analytics" (defensive)
		const cleanup = onConsentChange(({ accepted, rejected }) => {
			if (rejected.includes("analytics")) {
				stop();
			} else if (accepted.includes("analytics")) {
				start().catch(console.error);
			}
		});

		return () => {
			stop();
			cleanup();
		};
	}, [apiKey]);

	return useMemo(
		() => <Provider value={value}>{children}</Provider>,
		[value, children],
	);
};
